import { useEffect } from 'react';
/**
 * Repairs the document root's overflow so `position: sticky` can work.
 *
 * WordPress-backed story pages carry a stylesheet — injected inside the body
 * via `dangerouslySetInnerHTML`, so it lands *after* Emotion's head styles and
 * wins the cascade at equal specificity — which sets `overflow-x: hidden` on
 * html/body. Per spec a non-visible value on one axis forces the other to
 * `auto`, so both elements compute to `overflow: hidden auto` and become scroll
 * containers. body's height equals its own content height, so that scrollport
 * never scrolls (the document scroll lives on the viewport), and any `sticky`
 * descendant resolves against it and silently stops sticking. Measured on next
 * (/hub/bundeswehr/neurochirurgie): the AMA sidebar's rect top equalled its
 * flex row's top with 3816px of sticky range still available.
 *
 * `clip` suppresses horizontal overflow exactly like `hidden` but does not
 * create a scrollport, so sticky keeps working. `overflow-y` has to be restated
 * as `visible`, or the forced `auto` keeps the element a scroll container.
 *
 * Applied as an inline `!important` declaration rather than through
 * `GlobalStyles`: a stylesheet rule loses to the WordPress `<style>` that comes
 * later in the document, and matching its specificity would be a guessing game
 * against markup we do not control.
 *
 * Only pages that are actually broken are touched — if `overflow-x` is not
 * `hidden` there is no dead scrollport to repair, and forcing `clip` could clip
 * content that was legitimately allowed to overflow.
 */
export const useStickyScrollportFix = (enabled) => {
    useEffect(() => {
        if (!enabled)
            return;
        const restores = [];
        const override = (el, property, value) => {
            const previousValue = el.style.getPropertyValue(property);
            const previousPriority = el.style.getPropertyPriority(property);
            restores.push(() => {
                if (previousValue) {
                    el.style.setProperty(property, previousValue, previousPriority);
                }
                else {
                    el.style.removeProperty(property);
                }
            });
            el.style.setProperty(property, value, 'important');
        };
        for (const el of [document.documentElement, document.body]) {
            if (getComputedStyle(el).overflowX !== 'hidden')
                continue;
            override(el, 'overflow-x', 'clip');
            override(el, 'overflow-y', 'visible');
        }
        return () => {
            // Reverse order so nested saves restore cleanly.
            restores.reverse().forEach((restore) => restore());
        };
    }, [enabled]);
};
